import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, Zap, Shield, CreditCard, Trophy, Crown, Sparkles } from 'lucide-react';
import { CountdownTimer } from './CountdownTimer';
import { trackEvent } from '../lib/fbPixel';
import { goToCheckout } from '../lib/checkout';
interface PricingSectionProps {
  onCTAClick: () => void;
}
type PackId = 'profesional' | 'elite';
export function PricingSection({
  onCTAClick
}: PricingSectionProps) {
  const [selected, setSelected] = useState<PackId>('elite');
  const packs = [{
    id: 'profesional' as PackId,
    icon: '📘',
    title: 'PACK PROFESIONAL',
    desc: '12 Tomos + 6 Bonus',
    oldPrice: '$29.990',
    price: '$18.990',
    isElite: false
  }, {
    id: 'elite' as PackId,
    icon: '🏆',
    title: 'PACK ÉLITE TOTAL',
    desc: '12 Tomos + 6 Bonus + 2 Élite',
    oldPrice: '$49.990',
    price: '$27.990',
    savings: 'AHORRÁS $9.000',
    isElite: true
  }];
  const ctaText = selected === 'elite' ? '→ SÍ, QUIERO EL PACK ÉLITE TOTAL' : '→ SÍ, QUIERO EL PACK PROFESIONAL';
  const handlePurchaseClick = () => {
    const pack = packs.find(p => p.id === selected);
    const value = selected === 'elite' ? 27990 : 18990;
    trackEvent('InitiateCheckout', {
      content_name: pack?.title ?? 'VOLEY 360',
      content_category: 'Pack',
      content_ids: [selected],
      value,
      currency: 'ARS',
      num_items: 1
    });
    goToCheckout(selected);
  };
  return <section id="pricing" className="w-full px-4 py-20 md:py-24 bg-bg-primary" data-id="element-206">
      <div className="max-w-3xl mx-auto" data-id="element-207">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-10" data-id="element-208">
          <h2 className="text-4xl md:text-5xl font-black mb-4" data-id="element-209">
            Elegí tu <span className="text-orange" data-id="element-210">Pack</span>
          </h2>
          <p className="text-lg text-text-secondary" data-id="element-211">
            Ambas opciones incluyen los 6 Bonus estratégicos
          </p>
        </motion.div>

        {/* Stacked radio-card packs */}
        <div className="space-y-4 mb-8" data-id="element-212">
          {packs.map((pack, index) => {
          const isSelected = selected === pack.id;
          return <motion.button key={pack.id} type="button" onClick={() => setSelected(pack.id)} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className={`w-full text-left bg-bg-card rounded-card p-5 md:p-6 relative transition-all ${isSelected ? pack.isElite ? 'border-2 border-orange' : 'border-2 border-orange' : 'border-2 border-border-card hover:border-text-secondary/40'}`} style={isSelected && pack.isElite ? {
            boxShadow: '0 0 30px rgba(168, 216, 234, 0.25)'
          } : undefined} data-id="element-213">
                {/* MÁS ELEGIDO badge */}
                {pack.isElite && <div className="absolute -top-3 right-4" data-id="element-214">
                    <div className="px-3 py-1 bg-gold text-bg-primary font-black rounded-md text-xs flex items-center gap-1 shadow-lg" data-id="element-215">
                      <Sparkles className="w-3 h-3" data-id="element-216" />
                      MÁS ELEGIDO
                    </div>
                  </div>}

                <div className="flex items-center gap-4" data-id="element-217">
                  {/* Radio circle */}
                  <div className={`w-6 h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all ${isSelected ? 'border-orange' : 'border-text-secondary/50'}`} data-id="element-218">
                    {isSelected && <div className="w-3 h-3 rounded-full bg-orange" data-id="element-219" />}
                  </div>

                  {/* Title + desc */}
                  <div className="flex-1 min-w-0" data-id="element-220">
                    <div className="flex items-center gap-2 mb-1" data-id="element-221">
                      <span className="text-xl" data-id="element-222">{pack.icon}</span>
                      <h3 className="font-black text-base md:text-lg" data-id="element-223">
                        {pack.title}
                      </h3>
                    </div>
                    <p className="text-sm text-text-secondary" data-id="element-224">{pack.desc}</p>
                    {pack.savings && <div className="inline-block mt-2 px-3 py-1 bg-orange/15 border border-orange/40 rounded-full text-orange text-xs font-bold" data-id="element-225">
                        {pack.savings}
                      </div>}
                  </div>

                  {/* Price */}
                  <div className="text-right flex-shrink-0" data-id="element-226">
                    <div className="text-sm text-text-secondary line-through" data-id="element-227">
                      {pack.oldPrice}
                    </div>
                    <div className="text-2xl md:text-3xl font-black" data-id="element-228">
                      {pack.price}
                    </div>
                    <div className="text-xs text-text-secondary" data-id="element-229">ARS</div>
                  </div>
                </div>
              </motion.button>;
        })}
        </div>

        {/* Countdown line */}
        <div className="flex items-center justify-center gap-3 mb-6 text-orange" data-id="element-230">
          <span className="text-xl" data-id="element-231">⏰</span>
          <span className="font-bold text-sm md:text-base" data-id="element-232">
            Precio de lanzamiento termina en
          </span>
          <CountdownTimer variant="compact" data-id="element-233" />
        </div>

        {/* Big CTA */}
        <motion.button onClick={handlePurchaseClick} whileHover={{
        scale: 1.01
      }} whileTap={{
        scale: 0.99
      }} className="w-full py-5 md:py-6 bg-orange hover:bg-orange/90 text-bg-primary text-base md:text-xl font-black rounded-card shadow-2xl transition-colors mb-6" data-id="element-234">
          {ctaText}
        </motion.button>

        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-text-secondary mb-12 mt-4" data-id="element-235">
          <div className="flex items-center gap-2" data-id="element-236">
            <Lock className="w-4 h-4" data-id="element-237" />
            <span data-id="element-238">Pago seguro</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-239">
            <Zap className="w-4 h-4" data-id="element-240" />
            <span data-id="element-241">Acceso en 5 min</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-242">
            <Shield className="w-4 h-4" data-id="element-243" />
            <span data-id="element-244">Garantía 7 días</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-245">
            <CreditCard className="w-4 h-4" data-id="element-246" />
            <span data-id="element-247">Tarjeta · Transferencia</span>
          </div>
        </div>

        {/* What does the Elite pack add? */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-10" data-id="element-248">
          <h3 className="text-3xl md:text-4xl font-black mb-3" data-id="element-249">
            ¿Qué agrega el{' '}
            <span className="text-orange" data-id="element-250">Pack Élite Total?</span>
          </h3>
          <p className="text-text-secondary" data-id="element-251">
            Por solo $10.000 más, llevás tu carrera al siguiente nivel.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-252">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-bg-card border-2 border-gold rounded-card p-6" data-id="element-253">
            <Crown className="w-8 h-8 text-gold mb-3" data-id="element-254" />
            <h4 className="text-lg font-black text-gold mb-2" data-id="element-255">
              Élite 1: Planificador Anual Día por Día
            </h4>
            <p className="text-sm text-text-secondary" data-id="element-256">
              De Enero a Diciembre, todas tus sesiones organizadas. Nunca más
              pensás "¿qué entreno hoy?"
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-bg-card border-2 border-gold rounded-card p-6" data-id="element-257">
            <Trophy className="w-8 h-8 text-gold mb-3" data-id="element-258" />
            <h4 className="text-lg font-black text-gold mb-2" data-id="element-259">
              Élite 2: Sponsors, Cuota y Financiamiento
            </h4>
            <p className="text-sm text-text-secondary" data-id="element-260">
              Guiones para conseguir sponsors, subir la cuota sin quejas y
              financiar tu equipo en 90 días.
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
}